import React, { useMemo, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import { green } from "@mui/material/colors";

const SELECT_ALL_KEY = "__select_all__";
const SELECT_ALL_OPTION = { key: SELECT_ALL_KEY, name: "Select All" };

/**
 * Purpose-built multi-select for the Report "Stores" filter.
 *
 * The shared SelectInputField / SearchInputFieldWithDebounce both hand their
 * option list to MUI Autocomplete's default `filterOptions`, which matches
 * every option's label (including the injected "Select All" pseudo-option)
 * against whatever text is typed. Since "Select All" never matches a real
 * search term, it silently disappears from the dropdown the moment you search.
 *
 * Here filtering is done locally (this component owns `query`) and
 * `filterOptions` is disabled — "Select All" is prepended after filtering, so
 * it's always visible, and it only ever toggles the currently-filtered/visible
 * stores (not the full, possibly much larger, unfiltered list).
 */
const StoreMultiSelect = ({ options, value, onChange, width = "100%", placeholder = "Stores" }) => {
	const [query, setQuery] = useState("");
	const allOptions = options || [];
	const selectedKeys = value || [];

	const filteredOptions = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return allOptions;
		return allOptions.filter((o) => (o.name || "").toLowerCase().includes(q));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allOptions, query]);

	const filteredKeys = useMemo(() => filteredOptions.map((o) => o.key), [filteredOptions]);
	const isAllFilteredSelected = filteredKeys.length > 0 && filteredKeys.every((k) => selectedKeys.includes(k));
	const isSomeFilteredSelected = filteredKeys.some((k) => selectedKeys.includes(k));
	const hasValues = selectedKeys.length > 0;

	const autocompleteOptions = useMemo(
		() => (filteredOptions.length ? [SELECT_ALL_OPTION, ...filteredOptions] : filteredOptions),
		[filteredOptions]
	);

	const selectedOptions = useMemo(
		() => allOptions.filter((o) => selectedKeys.includes(o.key)),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[allOptions, selectedKeys]
	);

	const handleChange = (event, newValue) => {
		const lastPicked = newValue[newValue.length - 1];
		if (lastPicked?.key === SELECT_ALL_KEY) {
			// Only touch stores currently matching the search — anything selected
			// earlier from a different search stays untouched either way.
			onChange(
				isAllFilteredSelected
					? selectedKeys.filter((k) => !filteredKeys.includes(k))
					: [...new Set([...selectedKeys, ...filteredKeys])]
			);
			return;
		}
		onChange(newValue.filter((o) => o.key !== SELECT_ALL_KEY).map((o) => o.key));
	};

	return (
		<Autocomplete
			multiple
			disableCloseOnSelect
			size="small"
			fullWidth
			options={autocompleteOptions}
			filterOptions={(opts) => opts}
			getOptionLabel={(option) => option.name}
			isOptionEqualToValue={(opt, val) => opt.key === val.key}
			value={selectedOptions}
			inputValue={query}
			onChange={handleChange}
			onInputChange={(event, newInputValue, reason) => {
				if (reason === "input") setQuery(newInputValue);
			}}
			renderTags={(tags) => (
				<span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
					{tags.map((opt) => opt.name).join(", ")}
				</span>
			)}
			renderOption={(props, option, { selected }) => {
				const isSelectAll = option.key === SELECT_ALL_KEY;
				const isChecked = isSelectAll ? isAllFilteredSelected : selected;
				const isIndeterminate = isSelectAll && isSomeFilteredSelected && !isAllFilteredSelected;
				return (
					<li {...props} key={option.key}>
						<Checkbox
							checked={isChecked}
							indeterminate={isIndeterminate}
							sx={{ margin: 0, padding: "2px", mr: 1 }}
							disableRipple
						/>
						<Typography fontSize={13} color={isSelectAll ? green[600] : "inherit"}>
							{option.name}
						</Typography>
					</li>
				);
			}}
			renderInput={(params) => <TextField {...params} size="small" placeholder={placeholder} variant="outlined" />}
			sx={{
				width,
				"& .MuiAutocomplete-inputRoot": {
					display: "flex",
					alignItems: "center",
					flexWrap: "nowrap",
					height: 40,
					overflow: "hidden",
				},
				"& .MuiAutocomplete-tag": {
					flex: hasValues ? "1 1 100%" : "0 0 0%",
					overflow: "hidden",
					whiteSpace: "nowrap",
					textOverflow: "ellipsis",
					marginRight: "4px",
					transition: "all 0.3s ease",
					display: hasValues ? "flex" : "none",
				},
				"& .MuiAutocomplete-input": {
					flex: hasValues ? "0 0 0%" : "1 1 100%",
					minWidth: 0,
					width: hasValues ? "1px" : "100%",
					pointerEvents: hasValues ? "none" : "auto",
					transition: "all 0.3s ease",
					display: hasValues ? "none" : "block",
					padding: 0,
				},
				"&.Mui-focused .MuiAutocomplete-input, &:hover .MuiAutocomplete-input": hasValues
					? { flex: "0 0 40%", width: "auto", pointerEvents: "auto", display: "block" }
					: {},
				"&.Mui-focused .MuiAutocomplete-tag, &:hover .MuiAutocomplete-tag": hasValues
					? { flex: "0 0 60%", maxWidth: "60%", display: "flex" }
					: {},
			}}
		/>
	);
};

export default StoreMultiSelect;
